import activityReportsService from '../../api/activity-reports-service';
import { removeArrayItem, updateArrayItem } from '../../util/array';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '../../util/event-bus';
import i18n from '../../i18n/i18n-config';
import { mapErrorsToInputs } from '../../util/forms';

const state = {
  activityReports: null,
  activityReportsPagination: {},
  editedActivityReport: {},
  activityReportValidationErrors: {},
  activityReportsFilterParams: {},
  loadingActivityReports: false,
  savingActivityReport: false,
  savingFile: false,
};

const mutations = {
  SET_ACTIVITY_REPORTS(state, { data, current_page, per_page, total }) {
    state.activityReports = data;
    state.activityReportsPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.activityReportsFilterParams = params;
  },

  STORE_ACTIVITY_REPORT(state, report) {
    state.activityReports = [...state.activityReports, report];
    state.activityReportValidationErrors = {};
    state.activityReportsPagination.total += 1;
  },

  SET_EDITED_ACTIVITY_REPORT(state, report) {
    state.activityReportValidationErrors = {};
    state.editedActivityReport = JSON.parse(JSON.stringify(report));
  },

  UPDATE_ACTIVITY_REPORT(state, report) {
    state.activityReports = updateArrayItem(state.activityReports, report);
  },

  DELETE_ACTIVITY_REPORT(state, report) {
    state.activityReports = removeArrayItem(state.activityReports, report);
    state.activityReportsPagination.total -= 1;
  },

  SET_ACTIVITY_REPORT_VALIDATION_ERRORS(state, validationErrors) {
    state.activityReportValidationErrors = validationErrors;
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.activityReportValidationErrors[field];
  },

  SET_LOADING_ACTIVITY_REPORTS(state, loading) {
    state.loadingActivityReports = loading;
  },

  SET_SAVING_ACTIVITY_REPORT(state, saving) {
    state.savingActivityReport = saving;
  },
};

const actions = {
  fetchActivityReports({ commit }, params) {
    commit('SET_LOADING_ACTIVITY_REPORTS', true);
    return activityReportsService
      .getPage(params)
      .then((res) => {
        commit('SET_ACTIVITY_REPORTS', res.data);
        if (params.page !== state.activityReportsFilterParams.page) {
          window.scrollTo(0, 0);
        }
        commit('SET_FILTER_PARAMS', params);
      })
      .finally(() => {
        commit('SET_LOADING_ACTIVITY_REPORTS', false);
      });
  },

  async storeActivityReport({ commit }) {
    commit('SET_SAVING_ACTIVITY_REPORT', true);
    const res = await activityReportsService.create();
    commit('SET_SAVING_ACTIVITY_REPORT', false);
    commit('STORE_ACTIVITY_REPORT', res.data);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('activity_report_created'));
    return res;
  },

  editActivityReport({ state, commit }, reportId) {
    const report = state.activityReports?.find((p) => p.id === reportId);
    if (report) {
      commit('SET_EDITED_ACTIVITY_REPORT', report);
      return Promise.resolve(report);
    }
    return activityReportsService.getById(reportId).then((res) => {
      commit('SET_EDITED_ACTIVITY_REPORT', res.data);
    });
  },

  updateActivityReport({ commit }, report) {
    commit('SET_SAVING_ACTIVITY_REPORT', true);
    return activityReportsService
      .update(report)
      .then((res) => {
        commit('UPDATE_ACTIVITY_REPORT', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('activity_report_updated'));
      })
      .catch((err) => {
        commit('SET_ACTIVITY_REPORT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      })
      .finally(() => commit('SET_SAVING_ACTIVITY_REPORT', false));
  },

  deleteActivityReport({ commit }, report) {
    openConfirmDialog({
      title: i18n.t('confirm_activity_report_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      activityReportsService.delete(report).then(() => {
        commit('DELETE_ACTIVITY_REPORT', report);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('activity_report_deleted'));
      });
    });
  },

  clearActivityReportValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
